import React, { useCallback, useEffect, useState } from 'react';

import './ConfirmDialog.css';

export type ConfirmDialogOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  showCancel?: boolean;
};

type PendingConfirm = {
  kind: 'confirm';
  options: ConfirmDialogOptions;
  resolve: (value: boolean) => void;
};

type PendingNotice = {
  kind: 'notice';
  options: ConfirmDialogOptions;
  resolve: () => void;
};

type PendingDialog = PendingConfirm | PendingNotice;

type ConfirmDialogModalProps = ConfirmDialogOptions & {
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmDialogModal: React.FC<ConfirmDialogModalProps> = ({
  title = 'Confirm Action',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  showCancel = true,
  onCancel,
  onConfirm,
}) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
      if (event.key === 'Enter') onConfirm();
    };

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onCancel, onConfirm]);

  return (
    <div className="confirm-dialog__overlay" onClick={onCancel}>
      <div className="confirm-dialog__card" onClick={(event) => event.stopPropagation()}>
        <p className="confirm-dialog__eyebrow">Please Confirm</p>
        <h3 className="confirm-dialog__title">{title}</h3>
        <p className="confirm-dialog__message">{message}</p>
        <div className="confirm-dialog__actions">
          {showCancel ? (
            <button className="confirm-dialog__button confirm-dialog__button--secondary" onClick={onCancel} type="button">
              {cancelLabel}
            </button>
          ) : null}
          <button
            className={`confirm-dialog__button ${danger ? 'confirm-dialog__button--danger' : 'confirm-dialog__button--primary'}`}
            onClick={onConfirm}
            type="button"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const useConfirmDialog = () => {
  const [pending, setPending] = useState<PendingDialog | null>(null);

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ kind: 'confirm', options, resolve });
    });
  }, []);

  const notify = useCallback((options: ConfirmDialogOptions) => {
    return new Promise<void>((resolve) => {
      setPending({
        kind: 'notice',
        options: {
          confirmLabel: 'OK',
          showCancel: false,
          title: 'Notice',
          ...options,
        },
        resolve,
      });
    });
  }, []);

  const handleCancel = useCallback(() => {
    setPending((current) => {
      if (!current) return null;
      if (current.kind === 'confirm') {
        current.resolve(false);
      } else {
        current.resolve();
      }
      return null;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setPending((current) => {
      if (!current) return null;
      if (current.kind === 'confirm') {
        current.resolve(true);
      } else {
        current.resolve();
      }
      return null;
    });
  }, []);

  const confirmDialog = pending ? (
    <ConfirmDialogModal
      cancelLabel={pending.options.cancelLabel}
      confirmLabel={pending.options.confirmLabel}
      danger={pending.options.danger}
      message={pending.options.message}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      showCancel={pending.kind === 'confirm' ? pending.options.showCancel !== false : false}
      title={pending.options.title}
    />
  ) : null;

  return { confirm, notify, confirmDialog };
};
